import type {
  AuthProvider,
  CreateParams,
  DataProvider,
  DeleteManyParams,
  DeleteParams,
  Identifier,
  UpdateManyParams,
  UpdateParams,
} from 'react-admin';
import { getAmbientActiveRegion } from '../auth/activeRegion';
import {
  REDACTED_MARKER,
  SENSITIVE_AUDIT_FIELDS,
  type AdminDataProvider,
  type AuditAction,
  type AuditEntryDraft,
  type AuditSink,
} from './types';

/**
 * THE AUDIT FLOOR (D-audit).
 *
 * `withAudit` wraps a DataProvider so that EVERY mutation through it —
 * create / update / updateMany / delete / deleteMany — automatically writes an
 * audit entry. This sits at the provider seam on purpose: a surface cannot forget
 * to log, because logging is not the surface's job. If a write reaches the
 * provider, an entry exists. There are no opt-outs.
 *
 * THE SWAP SEAM. The wrapper is composed over the provider in ./index.ts:
 *   withAudit(createMockDataProvider(...), { authProvider, sink })
 * When the real admin API lands, two clean paths exist:
 *   - keep auditing client-side: `withAudit(realProvider, ...)` unchanged; or
 *   - the API audits server-side (the REAL enforcement point): drop the wrapper
 *     and `getList('audit')` reads the server's trail. The read path
 *     (getList('audit')) is already a normal provider call, so it survives either
 *     way — only the automatic WRITE is the interim client-side piece.
 *
 * ACTOR & REGION (automatic means automatic):
 *   - Identity (id/email) and role come from the `authProvider`
 *     (getIdentity / getPermissions) — the token is the source of truth, so no
 *     surface ever passes actor info.
 *   - The active region is a UI selection (a GLOBAL admin's switcher choice), so
 *     it cannot live in the token; it is read from the ambient holder that
 *     RegionProvider keeps in sync (../auth/activeRegion.ts).
 *
 * DIFF & REDACTION:
 *   - before/after hold ONLY the changed fields (a one-field edit → a one-field
 *     diff), never whole records (delete records the removed record as `before`).
 *   - Sensitive opaque fields (payoutDetails-class, see SENSITIVE_AUDIT_FIELDS)
 *     are NEVER snapshotted by value: a change records the field NAME with
 *     REDACTED_MARKER, proving a change happened without leaking the value.
 *
 * Auditing must never break the mutation it records: entry construction is
 * wrapped so a failure drops the entry, not the write.
 */

export interface WithAuditDeps {
  /** Source of the acting admin's identity + role. */
  authProvider: Pick<AuthProvider, 'getIdentity' | 'getPermissions'>;
  /** Where entries are written (the mock store's sink, or a real no-op). */
  sink: AuditSink;
  /** Active-region resolver; defaults to the ambient holder. */
  getRegion?: () => string | null;
  /** Clock for occurredAt; defaults to real time. Injectable for tests. */
  now?: () => string;
}

type Fields = Record<string, unknown>;

const isEqual = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** A sensitive field's value is replaced by the marker; others pass through. */
const redact = (key: string, value: unknown): unknown =>
  SENSITIVE_AUDIT_FIELDS.has(key) ? REDACTED_MARKER : value;

/**
 * before/after of ONLY the changed fields (`id` excluded). With no prior
 * snapshot (e.g. create, or an update with no previousData) the incoming fields
 * are recorded as `after` and `before` is null.
 */
const diffChangedFields = (
  prev: Fields | null | undefined,
  next: Fields,
): { before: Fields | null; after: Fields } => {
  const after: Fields = {};
  if (prev == null) {
    for (const key of Object.keys(next)) {
      if (key === 'id') continue;
      after[key] = redact(key, next[key]);
    }
    return { before: null, after };
  }
  const before: Fields = {};
  for (const key of Object.keys(next)) {
    if (key === 'id') continue;
    if (!isEqual(prev[key], next[key])) {
      before[key] = redact(key, prev[key]);
      after[key] = redact(key, next[key]);
    }
  }
  return { before, after };
};

/** The whole removed record as `before`, with sensitive fields redacted. */
const snapshotForDelete = (record: Fields): Fields => {
  const before: Fields = {};
  for (const key of Object.keys(record)) before[key] = redact(key, record[key]);
  return before;
};

/** Best-effort snapshot of many records by id, taken BEFORE the mutation. */
const snapshotMany = async (
  inner: DataProvider,
  resource: string,
  ids: readonly Identifier[],
): Promise<Map<Identifier, Fields>> => {
  const map = new Map<Identifier, Fields>();
  try {
    const { data } = await inner.getMany(resource, { ids: [...ids] });
    for (const row of data) map.set(row.id, row as Fields);
  } catch {
    // best-effort: a missing before-snapshot just yields a null `before`
  }
  return map;
};

const safeGetOne = async (
  inner: DataProvider,
  resource: string,
  id: Identifier,
): Promise<Fields | undefined> => {
  try {
    const { data } = await inner.getOne(resource, { id });
    return data as Fields;
  } catch {
    return undefined;
  }
};

export function withAudit(
  inner: AdminDataProvider,
  deps: WithAuditDeps,
): AdminDataProvider {
  const getRegion = deps.getRegion ?? getAmbientActiveRegion;
  const now = deps.now ?? (() => new Date().toISOString());

  const resolveActor = async (): Promise<{
    id: string;
    email: string;
    role: string | null;
  }> => {
    try {
      const identity = await deps.authProvider.getIdentity?.();
      const permissions = await deps.authProvider.getPermissions?.({});
      const role = typeof permissions === 'string' ? permissions : null;
      const id = identity?.id != null ? String(identity.id) : 'unknown';
      const rawEmail = (identity as { email?: unknown } | undefined)?.email;
      const email =
        typeof rawEmail === 'string' ? rawEmail : (identity?.fullName ?? '');
      return { id, email: String(email), role };
    } catch {
      return { id: 'unknown', email: '', role: null };
    }
  };

  const write = async (
    action: AuditAction,
    resource: string,
    entries: ReadonlyArray<{
      recordId: Identifier;
      before: Fields | null;
      after: Fields | null;
    }>,
  ): Promise<void> => {
    // The audit trail is never itself audited (defensive — appendAuditEntries is
    // not a mutation method, so there is no recursion path anyway).
    if (resource === 'audit' || resource === 'auditLog') return;
    if (entries.length === 0) return;
    try {
      const actor = await resolveActor();
      const region = getRegion();
      const occurredAt = now();
      const drafts: AuditEntryDraft[] = entries.map((e) => ({
        actorId: actor.id,
        actorEmail: actor.email,
        actorRole: actor.role,
        action,
        resource,
        recordId: e.recordId,
        before: e.before,
        after: e.after,
        region,
        occurredAt,
      }));
      deps.sink.appendAuditEntries(drafts);
    } catch {
      // Auditing must never break the mutation it records.
    }
  };

  const provider = {
    ...inner,

    create: async (resource: string, params: CreateParams) => {
      const result = await inner.create(resource, params);
      await write('create', resource, [
        {
          recordId: result.data.id,
          before: null,
          after: diffChangedFields(null, params.data as Fields).after,
        },
      ]);
      return result;
    },

    update: async (resource: string, params: UpdateParams) => {
      const result = await inner.update(resource, params);
      const { before, after } = diffChangedFields(
        params.previousData as Fields | undefined,
        params.data as Fields,
      );
      await write('update', resource, [{ recordId: params.id, before, after }]);
      return result;
    },

    updateMany: async (resource: string, params: UpdateManyParams) => {
      // Snapshot the before-states BEFORE mutating so each diff is real.
      const prior = await snapshotMany(inner, resource, params.ids);
      const result = await inner.updateMany(resource, params);
      const next = params.data as Fields;
      await write(
        'update',
        resource,
        params.ids.map((id) => {
          const { before, after } = diffChangedFields(prior.get(id), next);
          return { recordId: id, before, after };
        }),
      );
      return result;
    },

    delete: async (resource: string, params: DeleteParams) => {
      const prior =
        (params.previousData as Fields | undefined) ??
        (await safeGetOne(inner, resource, params.id));
      const result = await inner.delete(resource, params);
      await write('delete', resource, [
        {
          recordId: params.id,
          before: prior ? snapshotForDelete(prior) : null,
          after: null,
        },
      ]);
      return result;
    },

    deleteMany: async (resource: string, params: DeleteManyParams) => {
      const prior = await snapshotMany(inner, resource, params.ids);
      const result = await inner.deleteMany(resource, params);
      await write(
        'delete',
        resource,
        params.ids.map((id) => {
          const rec = prior.get(id);
          return {
            recordId: id,
            before: rec ? snapshotForDelete(rec) : null,
            after: null,
          };
        }),
      );
      return result;
    },
  };

  return provider as unknown as AdminDataProvider;
}
